import React from "react";
import { AlertCircle, Loader2 } from "lucide-react";

import type { FileInfo } from "../../services/fileApi";

/** A file still on its way into a deal: picked, not yet stored. */
export interface PendingUpload {
	path: string;
	name: string;
	/** Queued behind another upload into the same deal. */
	waiting: boolean;
}

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/** Doppler is still classifying it — not ready to ask about yet. */
export const isFileBeingRead = (file: FileInfo): boolean =>
	file.classification_status === "pending" ||
	file.classification_status === "processing";

const StatusLabel: React.FC<{ text: string; title: string }> = ({ text, title }) => (
	<span
		className="ml-auto flex flex-shrink-0 items-center gap-1 text-xs font-medium text-primary"
		title={title}
	>
		<Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
		{text}
	</span>
);

/**
 * The right-hand side of a deal-room file row (DOP-608): its size once it is
 * ready, otherwise where it is — so an analyst can tell a file they just added
 * from one Doppler can already answer from.
 */
export const DealFileStatus: React.FC<{ file: FileInfo }> = ({ file }) => {
	if (isFileBeingRead(file)) {
		return (
			<StatusLabel
				text="Reading…"
				title="Doppler is reading this file. You can ask about it once it's ready."
			/>
		);
	}
	if (file.classification_status === "failed") {
		return (
			<span
				className="ml-auto flex flex-shrink-0 items-center gap-1 text-xs font-medium text-warning-fg"
				title={file.classification_error || "Doppler couldn't read this file."}
			>
				<AlertCircle className="h-3 w-3" aria-hidden="true" />
				Couldn't read
			</span>
		);
	}
	return file.file_size ? (
		<span className="ml-auto flex-shrink-0 text-xs text-[#6B7280]">
			{formatFileSize(file.file_size)}
		</span>
	) : null;
};

/** A row for a file that has not reached the deal yet. */
export const PendingUploadRow: React.FC<{ upload: PendingUpload }> = ({ upload }) => (
	<div className="flex items-center space-x-2 py-2 pl-6 pr-2 text-text-muted">
		<Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary-400" aria-hidden="true" />
		<span className="flex-1 truncate text-sm" title={upload.path}>
			{upload.name}
		</span>
		<span className="ml-auto flex-shrink-0 text-xs font-medium text-primary">
			{upload.waiting ? "Waiting to upload" : "Uploading…"}
		</span>
	</div>
);
